import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, FormControl, FormControlLabel, IconButton, Modal, Radio, RadioGroup, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { immersightBlue } from '../../utils/colors';

const useStyles = makeStyles((theme) => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  modalTitle: {
    fontSize: '1.3rem !important',
    fontWeight: 500,
    color: '#464646',
  },
  description: {
    marginTop: '1rem !important',
    color: '#666',
    fontSize: '0.95rem !important',
    marginBottom: '1rem !important',
    lineHeight: 1.5,
  },
  divider: {
    marginLeft: '-24px !important',
    marginRight: '-24px !important',
    backgroundColor: '#d3d3d3',
    height: '1px',
    marginBottom: theme.spacing(2),
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  conflictRow: {
    background: '#f9f9f9',
    borderRadius: 10,
    padding: theme.spacing(2),
    border: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column !important',
    gap: theme.spacing(1),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(3),
    marginTop: theme.spacing(2),
    borderTop: '1px solid #d3d3d3',
  },
  actionBtn: {
    backgroundColor: `${immersightBlue} !important`,
    color: '#fff !important',
    padding: theme.spacing(1, 4),
    fontSize: '0.9rem !important',
    borderRadius: 20,
    textTransform: 'uppercase',
    fontWeight: 600,
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
      color: 'rgba(0, 0, 0, 0.26) !important',
    },
  },
}));

export interface IConflictMeasurement {
  id: string | number;
  description: string;
  region: string; 
}

interface RegionConflictModalProps {
  open: boolean;
  planRegion: string; 
  conflictingMeasurements: IConflictMeasurement[];
  onConfirm: (decision: 'PLAN' | string) => void;
  onClose: () => void;
}

const RegionConflictModal: React.FC<RegionConflictModalProps> = ({
  open,
  planRegion,
  conflictingMeasurements,
  onConfirm,
  onClose,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  
  const [globalDecision, setGlobalDecision] = useState<'PLAN' | string>('PLAN');

  useEffect(() => {
    if (open) {
      setGlobalDecision('PLAN');
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(globalDecision);
  };

  const isMultiImport = conflictingMeasurements.length > 1;

  const uniqueConflictingRegions = Array.from(new Set(conflictingMeasurements.map(m => m.region)));

  return (
    <Modal open={open} onClose={onClose} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}>
      <Box className={classes.modalBox}>

        <div className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            {intl.formatMessage({ id: 'RegionConflict.title', defaultMessage: 'Regionen-Konflikt erkannt' })}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon style={{ color: '#464646' }} />
          </IconButton>
        </div>

        <Divider className={classes.divider} />

        <Typography className={classes.description}>
          {isMultiImport
            ? intl.formatMessage({ id: 'RegionConflict.descriptionMulti' }, { planRegion })
            : intl.formatMessage({ id: 'RegionConflict.descriptionSingle' }, { planRegion })
          }
        </Typography>

        <div className={classes.contentContainer}>
          <Box className={classes.conflictRow}>
            <FormControl component="fieldset">
              <RadioGroup
                className={classes.radioGroup}
                value={globalDecision}
                onChange={(e) => setGlobalDecision(e.target.value)}
              >
                <FormControlLabel
                  value="PLAN"
                  control={<Radio size="small" sx={{ color: immersightBlue, '&.Mui-checked': { color: immersightBlue } }} />}
                  label={<span style={{ fontSize: '0.9rem', color: '#464646' }}>{intl.formatMessage({ id: 'RegionConflict.optionPlan' }, { planRegion })}</span>}
                />
                
                {uniqueConflictingRegions.map(region => (
                  <FormControlLabel
                    key={region}
                    value={region}
                    control={<Radio size="small" sx={{ color: immersightBlue, '&.Mui-checked': { color: immersightBlue } }} />}
                    label={<span style={{ fontSize: '0.9rem', color: '#464646' }}>{intl.formatMessage({ id: 'RegionConflict.optionMeasurement' }, { region })}</span>}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </div>

        <div className={classes.footer}>
          <Button className={classes.actionBtn} onClick={handleConfirm}>
            {intl.formatMessage({ id: 'General.confirm', defaultMessage: 'BESTÄTIGEN' })}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default RegionConflictModal;